import React, { useEffect, useRef, useState } from 'react';
import { format, startOfYear } from 'date-fns';
import { useNavigate, useParams } from 'react-router-dom';
import { formatMoney } from '../../../../scripts/format.value.script';

import { UseOutletContextPlan } from './Plan';
import { StateLanguage } from '../../../../languages/config/StateLanguage';
import { Error } from '../../../../models/error.model';
import { Names } from '../../../../models/names.model';
import { AccountingEntry } from '../../../../models/accounting.entry.model';
import { GlobalSwal } from '../../../../styles/swal.style';
import { CompanyAccountingAccountEntries } from '../../../../models/company.accounting.account.entries.model';
import { AccountingAccount } from '../../../../models/accounting.account.model';
import { AccountingEntryDA } from '../../../../services/admin/accounting.entry.service';
import { getError } from '../../../../languages/translations/response';
import { dateFormat } from '../../../../scripts/date.time.format.script';
import { accountingAccountCode, accountingAccountName } from '../../../../scripts/item.account.script';
import { ApiDigitalAdminContact } from '../../../../services/admin/contact.service';
import { formatContact } from '../../../../scripts/format.value.script';
import { errorWebHandler } from '../../../../scripts/error.web.handler.script';
import { usePaginationUrl } from '../../../../hooks/usePaginationUrl';
import { triggerBlobDownload } from '../../../../scripts/export.blob.script';
import { exportFallbackFilename } from '../../../../scripts/document.filename.script';
import { saveLastPlanViewAccount } from './PlanAccountRedirect';

import AppPreloader from '../../../../components/Preloader';
import AppInputAccountingAccount from '../../../../components/element/InputAccountingAccount';
import AppAccountingFilterInterval from '../component/AccountingFilterInterval';
import AppLoading from '../../../../components/Loading';
import AppElementPagination from '../../../../components/element/ElementPagination';
import ButtonEntryDocuments from '../../../../components/button/ButtonEntryDocuments';
import DropdownActions from '../../../../components/dropdown/DropdownActions';

export interface PlanViewPageProps {};

let errorResponse: Error, accountingAccountEntriesResponse: CompanyAccountingAccountEntries;

interface BodyRow {
  date: string
  account: string
  account_name: string
  contact: string
  description: string
  debit: string
  credit: string
  balance: string
  debit_original: string
  credit_original: string
  balance_original: string
  has_conversion: boolean
  balance_approx: boolean
  is_reconciled?: boolean
  is_opening_balance?: boolean
  vouchers?: any
}

interface FootRow {
  date: string
  description: string
  debit: string
  credit: string
  balance: string
  debit_original: string
  credit_original: string
  balance_original: string
  incomplete_lines: number
}

const PlanViewPage: React.FunctionComponent<PlanViewPageProps> = props => {
  const {setRoute, companyForUser, accountingAccountsForCompany} = UseOutletContextPlan()
  const {lang} = StateLanguage()
  const navigate = useNavigate()
  const param = useParams()
  const paramId = param.id

  const { page: urlPage, setPage: setUrlPage } = usePaginationUrl()

  const [mounted, setMounted] = useState(false)
  const [filter, setFilter] = useState({interval: '', start_date: '', end_date: ''})
  const filterRef = useRef(filter)
  filterRef.current = filter
  const [includeOpeningBalance, setIncludeOpeningBalance] = useState(true)
  const includeOpeningBalanceRef = useRef(includeOpeningBalance)
  includeOpeningBalanceRef.current = includeOpeningBalance
  const [reconFilter, setReconFilter] = useState<'all' | 'true' | 'false'>('all')
  const reconFilterRef = useRef(reconFilter)
  reconFilterRef.current = reconFilter
  const [accounitngAccount, setAccountingAccount] = useState<AccountingAccount | null>(null)
  const [accounitngEntries, setAccountingEntries] = useState<AccountingEntry[] | null>(null)
  const [table, setTable] = useState<{head: any[], body: BodyRow[], foot: FootRow[]}>({head: [], body: [], foot: []})
  const [storage, setStorage] = useState<any>(null)
  const [pagination, setPagination] = useState({page: 1, limit: 20, total: 0, pages: 1})
  const [goToPage, setGoToPage] = useState('')
  const [exporting, setExporting] = useState(false)

  const handleSelectAccount = (accountId: string) => {
    if (companyForUser) {
      saveLastPlanViewAccount(companyForUser.id, accountId)
    }
    navigate(`/main/accounting/plan/view/${accountId}`)
  }

  const isForeignCurrency = accounitngAccount ? (accounitngAccount.currency?.code && accounitngAccount.currency.code !== 'CHF') : false
  const foreignCode = accounitngAccount?.currency?.code ?? ''

  const handleExport = async (format: 'xlsx' | 'pdf') => {
    if (!companyForUser || !paramId || exporting) return
    setExporting(true)
    try {
      const currentFilter = filterRef.current
      const accountName = accounitngAccount ? accounitngAccount.name[(companyForUser.language ?? 'fr').toLowerCase() as keyof Names] ?? '' : ''
      const period = currentFilter.start_date && currentFilter.end_date
        ? `${currentFilter.start_date}_${currentFilter.end_date}`
        : currentFilter.interval || ''
      const fallback = exportFallbackFilename(
        lang.labels.accountStatement, `${accountName}_${companyForUser.name}`, period, format
      )
      const result = await AccountingEntryDA.exportAccountEntries(companyForUser.id, paramId, format, {
        interval: currentFilter.interval || undefined,
        start_date: currentFilter.start_date || undefined,
        end_date: currentFilter.end_date || undefined,
        reconciled: reconFilterRef.current !== 'all' ? reconFilterRef.current : undefined,
        lang: lang.code.toLowerCase(),
        ...(includeOpeningBalanceRef.current === false && { include_opening_balance: 'false' }),
      }, fallback)
      if (result.blob && result.filename) {
        triggerBlobDownload(result.blob, result.filename)
      } else {
        GlobalSwal.fire({ title: result.error || lang.labels.exportError, icon: 'error', confirmButtonText: lang.labels.okGotIt })
      }
    } catch (error) {
      console.error('[PlanView] export failed', error)
      GlobalSwal.fire({ title: lang.labels.exportError, icon: 'error', confirmButtonText: lang.labels.okGotIt })
    } finally {
      setExporting(false)
    }
  }

  const loadAccountingContactEntries = async (id_company: string, id_accounting_account: string, page: number) => {
    const currentFilter = filterRef.current
    let tempHead: {date: string, description: string, debit: string, credit: string, balance: string, debit_original?: string, credit_original?: string, balance_original?: string}[] = []
    let tempBody: BodyRow[] = []
    let tempFoot: FootRow[] = []
    let endDate: string = (currentFilter.end_date.length > 0) ? currentFilter.end_date : format((new Date()), `yyyy-MM-dd'T'HH:mm:ss.SSSXXX`)

    let debitTotal: number = 0
    let creditTotal: number = 0
    let debitOriginalTotal: number = 0
    let creditOriginalTotal: number = 0

    await AccountingEntryDA.getAccountingAccountEntries(id_company, id_accounting_account, currentFilter.interval, currentFilter.start_date, currentFilter.end_date, page, pagination.limit, reconFilterRef.current, includeOpeningBalanceRef.current ? undefined : false).then( async (response) => {
      if (response.status === 200) {
        accountingAccountEntriesResponse = response.data

        localStorage.setItem('filter_accounting_account_entries', JSON.stringify({
          company_id: accountingAccountEntriesResponse.id_company,
          accounting_account_id: accountingAccountEntriesResponse.id_accounting_account,
          interval: accountingAccountEntriesResponse.filter.interval,
          start_date: accountingAccountEntriesResponse.filter.start_date,
          end_date: accountingAccountEntriesResponse.filter.end_date,
          include_opening_balance: includeOpeningBalanceRef.current,
        }))

        const respIncludeOB = (accountingAccountEntriesResponse as any).include_opening_balance
        const showOpeningBalance = respIncludeOB !== false

        if (showOpeningBalance && accountingAccountEntriesResponse.balance_previous.date.length > 0) {
          const ob = (accountingAccountEntriesResponse as any).opening_balance
          const obOrig = (accountingAccountEntriesResponse as any).opening_balance_original
          tempHead.push({
            date: dateFormat(lang.code, accountingAccountEntriesResponse.balance_previous.date),
            description: lang.labels.balancePrevious,
            debit: ob?.debit ? formatMoney(ob.debit) : '',
            credit: ob?.credit ? formatMoney(ob.credit) : '',
            balance: formatMoney(accountingAccountEntriesResponse.balance_previous.amount),
            debit_original: obOrig?.debit != null ? formatMoney(obOrig.debit) : undefined,
            credit_original: obOrig?.credit != null ? formatMoney(obOrig.credit) : undefined,
            balance_original: obOrig?.balance != null ? formatMoney(obOrig.balance) : undefined,
          })
        }

        let cumulApprox = false
        if (companyForUser && accountingAccountsForCompany) {
          // Resolve contact names per-page via unit getter
          const contactIds = [...new Set(
            accountingAccountEntriesResponse.accounting_entries
              .map((e: any) => e.id_contact)
              .filter((id: string) => id && id.length > 0)
          )]
          const contactMap: Record<string, string> = {}
          await Promise.all(contactIds.map(async (id: string) => {
            try {
              const resp = await ApiDigitalAdminContact.getContact(id_company, id)
              if (resp.status === 200 && resp.data) {
                contactMap[id] = formatContact(resp.data)
              } else {
                contactMap[id] = `[${id.slice(0, 8)}…]`
              }
            } catch {
              contactMap[id] = `[${id.slice(0, 8)}…]`
            }
          }))

          for (let accounitng_entry of accountingAccountEntriesResponse.accounting_entries) {
            const entry: any = accounitng_entry
            const counterpartAccount = entry.counterpart_account ?? ''
            // A line has original data if its relevant original field is present
            const hasDebitOrig = entry.account_debit_original != null
            const hasCreditOrig = entry.account_credit_original != null
            const hasOriginal = entry.account_debit ? hasDebitOrig : hasCreditOrig

            if (!hasOriginal) {
              cumulApprox = true
            }

            const debitOrig = hasDebitOrig ? entry.account_debit_original : (entry.account_debit ?? 0)
            const creditOrig = hasCreditOrig ? entry.account_credit_original : (entry.account_credit ?? 0)

            tempBody.push({
              date: dateFormat(lang.code, accounitng_entry.date),
              account: counterpartAccount ? accountingAccountCode(accountingAccountsForCompany, counterpartAccount) : '',
              account_name: counterpartAccount ? accountingAccountName(accountingAccountsForCompany, companyForUser.language, counterpartAccount) : '',
              contact: accounitng_entry.id_contact && accounitng_entry.id_contact.length > 0 ? (contactMap[accounitng_entry.id_contact] ?? '') : '',
              description: entry.description || '',
              debit: entry.account_debit ? formatMoney(entry.account_debit) : '',
              credit: entry.account_credit ? formatMoney(entry.account_credit) : '',
              balance: formatMoney(entry.balance ?? 0),
              debit_original: entry.account_debit ? formatMoney(debitOrig) : '',
              credit_original: entry.account_credit ? formatMoney(creditOrig) : '',
              balance_original: entry.balance_original != null ? formatMoney(entry.balance_original) : formatMoney(entry.balance ?? 0),
              has_conversion: hasOriginal,
              balance_approx: cumulApprox,
              is_reconciled: entry.is_reconciled,
              is_opening_balance: entry.is_opening_balance,
              vouchers: accounitng_entry.vouchers,
            })
            debitTotal += entry.account_debit ?? 0
            creditTotal += entry.account_credit ?? 0
            debitOriginalTotal += debitOrig
            creditOriginalTotal += creditOrig
          }
        }

        const periodDebit = accountingAccountEntriesResponse.period_totals?.debit ?? debitTotal
        const periodCredit = accountingAccountEntriesResponse.period_totals?.credit ?? creditTotal
        const balanceTotal = accountingAccountEntriesResponse.closing_balance?.balance ?? (accountingAccountEntriesResponse.balance_previous.amount + (debitTotal - creditTotal))

        const respData = accountingAccountEntriesResponse as any
        const origIncomplete = respData.original_incomplete_lines ?? 0

        tempFoot.push({
          date: dateFormat(lang.code, endDate),
          description: lang.labels.total,
          debit: formatMoney(periodDebit),
          credit: formatMoney(periodCredit),
          balance: formatMoney(balanceTotal),
          debit_original: formatMoney(respData.closing_balance_original?.debit ?? respData.period_totals_original?.debit ?? debitOriginalTotal),
          credit_original: formatMoney(respData.closing_balance_original?.credit ?? respData.period_totals_original?.credit ?? creditOriginalTotal),
          balance_original: formatMoney(respData.closing_balance_original?.balance ?? respData.period_totals_original?.balance ?? balanceTotal),
          incomplete_lines: origIncomplete,
        })

        setFilter(accountingAccountEntriesResponse.filter)
        setAccountingEntries(accountingAccountEntriesResponse.accounting_entries)
        setTable({head: tempHead, body: tempBody, foot: tempFoot})
        setPagination(accountingAccountEntriesResponse.pagination ?? {page: 1, limit: 20, total: 0, pages: 1})
        setStorage((localStorage.getItem('filter_accounting_account_entries')) ? JSON.parse(localStorage.getItem('filter_accounting_account_entries')!) : null)
      } else {
        errorResponse = response.data

        GlobalSwal.fire({
          title: getError(errorResponse.code, lang.code, errorResponse.message),
          text: lang.labels.sorryLooksLikeThereAreSomeErrorstryAgain,
          icon: 'error',
          confirmButtonText: lang.labels.okGotIt
        })
      }
    }).catch( (error) => {
      errorWebHandler(lang, error)
    })
  }

  useEffect( () => {
    if (companyForUser && paramId) {
      let filter_accounting_account_entries: any = (localStorage.getItem('filter_accounting_account_entries')) ? JSON.parse(localStorage.getItem('filter_accounting_account_entries')!) : null

      let dateCurrent: Date = new Date()
      let yearCurrent: Date = startOfYear(dateCurrent)

      const restoredInterval = (filter_accounting_account_entries && filter_accounting_account_entries.company_id === companyForUser.id && filter_accounting_account_entries.interval.length > 0) ? filter_accounting_account_entries.interval : ''
      const restoredStartDate = (filter_accounting_account_entries && filter_accounting_account_entries.company_id === companyForUser.id) ? (filter_accounting_account_entries.interval === 'all') ? format(yearCurrent, `yyyy-MM-dd'T'HH:mm:ss.SSSXXX`) : (filter_accounting_account_entries.start_date.length > 0) ? filter_accounting_account_entries.start_date : '' : ''
      const restoredEndDate = (filter_accounting_account_entries && filter_accounting_account_entries.company_id === companyForUser.id) ? (filter_accounting_account_entries.interval === 'all') ? format(dateCurrent, `yyyy-MM-dd'T'HH:mm:ss.SSSXXX`) : (filter_accounting_account_entries.end_date.length > 0) ? filter_accounting_account_entries.end_date : '' : ''

      const restoredIncludeOB = filter_accounting_account_entries?.include_opening_balance !== false
      setFilter({interval: restoredInterval, start_date: restoredStartDate, end_date: restoredEndDate})
      filterRef.current = {interval: restoredInterval, start_date: restoredStartDate, end_date: restoredEndDate}
      setIncludeOpeningBalance(restoredIncludeOB)
      includeOpeningBalanceRef.current = restoredIncludeOB

      setAccountingAccount(null)
      setAccountingEntries(null)
      setTable({head: [], body: [], foot: []})

      if (accountingAccountsForCompany && accountingAccountsForCompany.some((item: AccountingAccount) => (item.id === paramId))) {
        setAccountingAccount(accountingAccountsForCompany.find((item: AccountingAccount) => (item.id === paramId))!)
        saveLastPlanViewAccount(companyForUser.id, paramId)
        loadAccountingContactEntries(companyForUser.id, paramId, 1)
      }
    }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyForUser, accountingAccountsForCompany, paramId])

  useEffect( () => {
    if (companyForUser && paramId && accounitngAccount) {
      loadAccountingContactEntries(companyForUser.id, paramId, urlPage)
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlPage])

  useEffect( () => {
    setMounted(true)
    setRoute({path: {root: lang.labels.accounting, branch: lang.labels.accountStatements}, company: false})

    return () => setMounted(false)

  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  if (!mounted) return null

  const approxTooltip = lang.labels.approxBalanceTooltip.replace('{currency}', foreignCode)

  return (
    <div className="w-100 h-100">
      { companyForUser && accountingAccountsForCompany && accounitngAccount && accounitngEntries && param.id
        ?
        <>
          <div className="card card-flush mb-5">
            <div className="card-header">
              <div className="card-title">
                <div className="d-flex align-items-center">
                  <div className="symbol symbol-circle me-3">
                    <div className="symbol-label bg-transparent border border-secondary border-dashed">
                      <span className="svg-icon svg-icon-3x svg-icon-primary">
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path opacity="0.3" d="M22 8H8L12 4H19C19.6 4 20.2 4.39999 20.5 4.89999L22 8ZM3.5 19.1C3.8 19.7 4.4 20 5 20H12L16 16H2L3.5 19.1ZM19.1 20.5C19.7 20.2 20 19.6 20 19V12L16 8V22L19.1 20.5ZM4.9 3.5C4.3 3.8 4 4.4 4 5V12L8 16V2L4.9 3.5Z" fill="black"/>
                          <path d="M22 8L20 12L16 8H22ZM8 16L4 12L2 16H8ZM16 16L12 20L16 22V16ZM8 8L12 4L8 2V8Z" fill="black"/>
                        </svg>
                      </span>
                    </div>
                  </div>
                  <div className="d-flex flex-column">
                    <h4 className="mb-0">{accounitngAccount.name[(companyForUser.language ?? 'fr').toLowerCase() as keyof Names]}</h4>
                    <div className="text-muted fw-bold">
                      <small className="text-primary">{lang.labels.accountingAccount}</small>
                      <span className="mx-2">|</span>
                      <small className="text-primary">{accounitngAccount.code}</small>
                      <span className="mx-2">|</span>
                      <small className="text-primary">{lang.labels.accountingEntries}</small>
                      <span className="mx-2">|</span>
                      <small className="fw-bolder">{pagination.total > 0 ? pagination.total : accounitngEntries.length} {lang.labels.items}</small>
                    </div>
                  </div>
                </div>
              </div>
              <div className="card-toolbar">
                <div className="d-flex justify-content-end align-items-center gap-3">
                  <AppInputAccountingAccount
                    options={{small: true, type: 'generic', index: null, item: null}}
                    companyForUser={companyForUser}
                    genericList={accountingAccountsForCompany}
                    genericValue={paramId}
                    onSelectGenericAccount={handleSelectAccount}
                  />
                  <div className="d-flex align-items-center gap-1">
                    {(['all', 'false', 'true'] as const).map((val) => (
                      <button
                        key={val}
                        className={`btn btn-sm ${reconFilter === val ? 'btn-primary' : 'btn-light'}`}
                        onClick={() => {
                          setReconFilter(val)
                          reconFilterRef.current = val
                          if (urlPage === 1) {
                            loadAccountingContactEntries(companyForUser.id, paramId!, 1)
                          } else {
                            setUrlPage(1)
                          }
                        }}
                      >
                        {val === 'all' ? lang.labels.all : val === 'false' ? lang.labels.notReconciled : lang.labels.reconciled}
                      </button>
                    ))}
                  </div>
                  <AppAccountingFilterInterval storage={storage} filter={filter} setFilter={setFilter} loadAccountingEntries={() => { if (urlPage === 1) { loadAccountingContactEntries(companyForUser.id, paramId!, 1) } else { setUrlPage(1) } }} includeOpeningBalance={includeOpeningBalance} onIncludeOpeningBalanceChange={(val) => { setIncludeOpeningBalance(val); includeOpeningBalanceRef.current = val }}></AppAccountingFilterInterval>
                  <DropdownActions
                    label={exporting ? `${lang.labels.export}...` : lang.labels.export}
                    buttonClassName={`btn btn-sm ${exporting ? 'btn-light disabled' : 'btn-light btn-active-light-primary'}`}
                    items={[
                      { key: 'xlsx', label: `${lang.labels.excel} (.xlsx)`, onClick: () => handleExport('xlsx') },
                      { key: 'pdf', label: `${lang.labels.pdf} (.pdf)`, onClick: () => handleExport('pdf') },
                    ]}
                  />
                </div>
              </div>
            </div>
          </div>
          <div className="card">
            <div className="card-body p-0">
              <div className="dataTables_wrapper border border-2 rounded">
                <div className="table-responsive rounded">
                  <table className="table align-middle table-row-bordered gx-10 mb-2">
                    <thead className="bg-light border-bottom-2">
                      <tr className="text-dark text-uppercase fw-bolder fs-7">
                        <th className="w-100px text-start">{lang.labels.date}</th>
                        <th className="min-w-50px text-start">{lang.labels.account}</th>
                        <th className="min-w-150px text-start">{lang.labels.nameOfAccount}</th>
                        <th className="min-w-150px text-start">{lang.labels.contact}</th>
                        <th className="min-w-150px text-start">{lang.labels.description}</th>
                        <th className="min-w-125px text-end text-nowrap">{lang.labels.debit}{isForeignCurrency ? '' : ` (${companyForUser.currency?.code})`}</th>
                        <th className="min-w-125px text-end text-nowrap">{lang.labels.credit}{isForeignCurrency ? '' : ` (${companyForUser.currency?.code})`}</th>
                        <th className="min-w-125px text-end text-nowrap">{lang.labels.balance}{isForeignCurrency ? '' : ` (${companyForUser.currency?.code})`}</th>
                        <th className="w-50px text-center"><i className="bi bi-check2-square fs-4" title={lang.labels.reconciliation}></i></th>
                        <th className="w-50px text-center"><i className="bi bi-paperclip fs-4"></i></th>
                      </tr>
                    </thead>
                    { table
                      ?
                      <>
                        <tbody className="text-secondary fw-bold">
                          { table.head.map (( (item, index) => { return (
                            <tr key={index} className="row-bordered">
                              <td className="text-nowrap">{item.date}</td>
                              <td colSpan={4}>{item.description}</td>
                              <td className="text-end text-nowrap">
                                { isForeignCurrency && item.debit_original
                                  ? <>
                                      <div>{item.debit_original} {foreignCode}</div>
                                      <div className="text-muted fs-8">{item.debit} CHF</div>
                                    </>
                                  : item.debit
                                }
                              </td>
                              <td className="text-end text-nowrap">
                                { isForeignCurrency && item.credit_original
                                  ? <>
                                      <div>{item.credit_original} {foreignCode}</div>
                                      <div className="text-muted fs-8">{item.credit} CHF</div>
                                    </>
                                  : item.credit
                                }
                              </td>
                              <td className="text-end text-nowrap">
                                { isForeignCurrency && item.balance_original
                                  ? <>
                                      <div>{item.balance_original} {foreignCode}</div>
                                      <div className="text-muted fs-8">{item.balance} CHF</div>
                                    </>
                                  : item.balance
                                }
                              </td>
                              <td></td>
                              <td></td>
                            </tr>
                          )}))}
                        </tbody>
                        { table.body.length > 0
                          ?
                          <tbody className="text-secondary fw-bold">
                            { table.body.map (( (item, index) => { return (
                              <tr key={index} className="row-bordered">
                                <td className="text-nowrap">{item.date}</td>
                                <td>{item.account}</td>
                                <td>{item.account_name}</td>
                                <td>{item.contact}</td>
                                <td>{item.description}</td>
                                <td className="text-end text-nowrap">
                                  { isForeignCurrency && item.debit_original
                                    ? <>
                                        <div>
                                          {item.debit_original} {foreignCode}
                                          {!item.has_conversion && <span className="badge badge-light-warning fs-9 ms-1" title={lang.labels.partialBalance}>~</span>}
                                        </div>
                                        <div className="text-muted fs-8">{item.debit} CHF</div>
                                      </>
                                    : item.debit
                                  }
                                </td>
                                <td className="text-end text-nowrap">
                                  { isForeignCurrency && item.credit_original
                                    ? <>
                                        <div>
                                          {item.credit_original} {foreignCode}
                                          {!item.has_conversion && <span className="badge badge-light-warning fs-9 ms-1" title={lang.labels.partialBalance}>~</span>}
                                        </div>
                                        <div className="text-muted fs-8">{item.credit} CHF</div>
                                      </>
                                    : item.credit
                                  }
                                </td>
                                <td className="text-end text-nowrap">
                                  { isForeignCurrency
                                    ? <>
                                        <div>
                                          {item.balance_original} {foreignCode}
                                          {item.balance_approx && <span className="badge badge-light-warning fs-9 ms-1" title={approxTooltip}>~</span>}
                                        </div>
                                        <div className="text-muted fs-8">{item.balance} CHF</div>
                                      </>
                                    : item.balance
                                  }
                                </td>
                                <td className="text-center">
                                  { item.is_opening_balance
                                    ? <span className="badge badge-light-dark fs-9">{lang.labels.openingBalance}</span>
                                    : item.is_reconciled === true
                                      ? <span className="badge badge-light-success fs-9">{lang.labels.reconciled}</span>
                                      : item.is_reconciled === false
                                        ? <span className="badge badge-light-warning fs-9">{lang.labels.notReconciled}</span>
                                        : null
                                  }
                                </td>
                                <td className="text-center"><ButtonEntryDocuments vouchers={item.vouchers} /></td>
                              </tr>
                            )}))}
                          </tbody>
                          :
                          <tbody>
                            <tr className="text-secondary fw-bold row-bordered">
                              <td colSpan={10} className="text-center">{lang.labels.noRecordsFound}</td>
                            </tr>
                          </tbody>
                        }
                        <tfoot className="fw-bolder fs-7">
                          { table.foot.map (( (item, index) => { return (
                            <tr key={index}>
                              <td className="text-nowrap">{item.date}</td>
                              <td colSpan={4}>
                                {item.description}
                                { isForeignCurrency && item.incomplete_lines > 0 &&
                                  <span className="text-muted fw-normal fs-8 ms-2">
                                    — {lang.labels.partialLinesFooter.replace('{count}', String(item.incomplete_lines))}
                                  </span>
                                }
                              </td>
                              <td className="text-end text-nowrap">
                                { isForeignCurrency
                                  ? <>
                                      <div>{item.debit_original} {foreignCode}</div>
                                      <div className="text-muted fs-8">{item.debit} CHF</div>
                                    </>
                                  : item.debit
                                }
                              </td>
                              <td className="text-end text-nowrap">
                                { isForeignCurrency
                                  ? <>
                                      <div>{item.credit_original} {foreignCode}</div>
                                      <div className="text-muted fs-8">{item.credit} CHF</div>
                                    </>
                                  : item.credit
                                }
                              </td>
                              <td className="text-end text-nowrap">
                                { isForeignCurrency
                                  ? <>
                                      <div>
                                        {item.balance_original} {foreignCode}
                                        {item.incomplete_lines > 0 && <span className="badge badge-light-warning fs-9 ms-1" title={approxTooltip}>~</span>}
                                      </div>
                                      <div className="text-muted fs-8">{item.balance} CHF</div>
                                    </>
                                  : item.balance
                                }
                              </td>
                              <td></td>
                              <td></td>
                            </tr>
                          )}))}
                        </tfoot>
                      </>
                      :
                      <tbody>
                        <tr>
                          <td colSpan={10}>
                            <AppLoading></AppLoading>
                          </td>
                        </tr>
                      </tbody>
                    }
                  </table>
                </div>
              </div>
            </div>
          </div>
          { reconFilter !== 'all' &&
            <div className="alert alert-warning d-flex align-items-center py-2 px-4 mt-3 mb-0">
              <i className="bi bi-info-circle me-2"></i>
              <small>{lang.labels.filteredTotalsWarning}</small>
            </div>
          }
          { pagination.pages > 1 &&
            <div className="d-flex justify-content-between align-items-center mt-4">
              <small className="text-muted">
                {pagination.total} {lang.labels.items}
              </small>
              <div className="d-flex align-items-center gap-3">
                <AppElementPagination pagination={pagination} onPageChange={(p) => setUrlPage(p)} />
                <div className="d-flex align-items-center gap-1">
                  <input
                    className="form-control form-control-sm"
                    style={{width: '60px'}}
                    type="number"
                    min={1}
                    max={pagination.pages}
                    placeholder="#"
                    value={goToPage}
                    onChange={(e) => setGoToPage(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        const target = Math.max(1, Math.min(pagination.pages, parseInt(goToPage, 10) || 1))
                        setUrlPage(target)
                        setGoToPage('')
                      }
                    }}
                  />
                </div>
              </div>
            </div>
          }
        </>
        :
        <AppPreloader></AppPreloader>
      }
    </div>
  )
};

export default PlanViewPage;
